import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventBusService, EventPayload } from 'src/app/shared/services/event-bus.service';
import { LocumsMarginCalculatorEvents } from './enums/locums-margin-calculator-events.enum';
import { LocumsMarginCalculatorStore } from './locums-margin-calculator.store';
import { MarginDetails } from 'src/app/recruiting/locums-margin-calculator/models/margin-details.model';
import { LocumsMarginCalculatorSlice } from './locums-margin-calculator.slice';
import { MarginSetup } from 'src/app/recruiting/locums-margin-calculator/models/margin-setup.model';
import { AdditionalRate } from 'src/app/recruiting/locums-margin-calculator/models/additional-rate.model';
import { CandidateDetails } from 'src/app/recruiting/locums-margin-calculator/models/candidate-details.model';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { withLatestFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LocumsMarginCalculatorReducer {
    private readonly eventBus = inject(EventBusService);
    private readonly marginCalculatorStore = inject(LocumsMarginCalculatorStore);
    private readonly destroyRef = inject(DestroyRef);
    private readonly _ldFeatureManager = inject(LDFeatureManager);

    private readonly enableSpecialtyPersistenceLocums1099$ = this._ldFeatureManager.isEnabled(
        FeatureFlag.EnableSpecialtyPersistenceLocums1099
    );

    private readonly novaLocumsLeave1099GmcDialog$ = this._ldFeatureManager.isEnabled(
        FeatureFlag.NovaLocumsLeave1099GmcDialog
    );

    constructor() {
        this.listenToLocums1099MarginCalculatorSaveEvent();
        this.listenToLocums1099MarginCalculatorSaveSucceededEvent();
        this.listenToLocums1099MarginCalculatorSaveFailedEvent();
        this.listenToExistingRecordBeingLoaded();
        this.listenToFreshRecordBeingLoaded();
        this.listenToLocums1099MarginCalculatorRestoreFromInitialStateEvent();
    }

    private listenToLocums1099MarginCalculatorSaveEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SAVE_BUTTON_CLICKED)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.marginCalculatorStore.setIsSaving(true);
            });
    }

    private listenToLocums1099MarginCalculatorSaveSucceededEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SAVE_SUCCEEDED)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: EventPayload) => {
                if (this.marginCalculatorStore.marginSetup().marginId === 0) {
                    this.marginCalculatorStore.updateMarginId(event.payload.id);
                }
                this.marginCalculatorStore.setIsSaving(false);
                this.marginCalculatorStore.setError(null);
            });
    }

    private listenToLocums1099MarginCalculatorSaveFailedEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SAVE_FAILED)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: EventPayload) => {
                this.marginCalculatorStore.setIsSaving(false);
                this.marginCalculatorStore.setError(event.payload);
            });
    }

    private listenToExistingRecordBeingLoaded() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_LOAD_MARGIN_DETAILS)
            .pipe(withLatestFrom(this.enableSpecialtyPersistenceLocums1099$), takeUntilDestroyed(this.destroyRef))
            .subscribe(([event, enableSpecialtyPersistenceLocums1099]: [EventPayload, boolean]) => {
                const payload = event.payload as { marginDetails: MarginDetails };
                if (enableSpecialtyPersistenceLocums1099) {
                    this.marginCalculatorStore.setInitialLoadingData({
                        ...payload.marginDetails,
                        candidateDetails: {
                            ...payload.marginDetails.candidateDetails,
                            expertiseId: payload.marginDetails.expertiseId,
                            expertiseYearsOfExperience: payload.marginDetails.expertiseYearsOfExperience
                        }
                    });
                } else {
                    this.marginCalculatorStore.setInitialLoadingData(event.payload.marginDetails as MarginDetails);
                }
            });
    }

    private listenToFreshRecordBeingLoaded() {
        const marginDetails = this.translateSnapshotToMarginDetails(this.marginCalculatorStore.getSnapshot());
        this.marginCalculatorStore.setInitialLoadingData(marginDetails);

        this.eventBus
            .on(LocumsMarginCalculatorEvents.JOB_DETAILS_LOADED)
            .pipe(withLatestFrom(this.novaLocumsLeave1099GmcDialog$), takeUntilDestroyed(this.destroyRef))
            .subscribe(([_, isEnabled]: [EventPayload, boolean]) => {
                if (isEnabled) {
                    this.marginCalculatorStore.setInitialLoadingData({
                        jobDetails: this.marginCalculatorStore.getSnapshot().jobDetails
                    });
                }
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.CANDIDATE_DETAILS_LOADED)
            .pipe(withLatestFrom(this.novaLocumsLeave1099GmcDialog$), takeUntilDestroyed(this.destroyRef))
            .subscribe(([event, isEnabled]: [EventPayload, boolean]) => {
                if (isEnabled) {
                    const candidateData = event.payload as CandidateDetails;
                    this.marginCalculatorStore.setInitialLoadingData({
                        candidateExpertiseId: candidateData.candidateExpertiseId ?? 0,
                        candidateDetails: candidateData,
                        expertiseId: candidateData.expertiseId ?? 0,
                        expertiseYearsOfExperience: candidateData.expertiseYearsOfExperience ?? 0
                    });
                } else {
                    this.marginCalculatorStore.setInitialLoadingData({
                        candidateExpertiseId: event.payload.candidateExpertiseId as number
                    });
                }
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.MARGIN_SETUP_PRE_POPULATE_BILL_RATE_LOADED)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: EventPayload) => {
                this.marginCalculatorStore.setInitialLoadingData({
                    marginSetUpDetails: {
                        ...((({ marginId, marginStatusId, ...rest }) => rest)(
                            this.marginCalculatorStore.getSnapshot().marginSetup
                        ) as MarginSetup),
                        billRate: event.payload as number
                    }
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_24_HOURS_PRE_POPULATE_BILL_RATE_LOADED)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: EventPayload) => {
                this.marginCalculatorStore.setInitialLoadingData({
                    marginAdditionalRates: [event.payload as AdditionalRate]
                });
            });
    }

    private listenToLocums1099MarginCalculatorRestoreFromInitialStateEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_RESTORE_FROM_INITIAL_STATE)
            .pipe(withLatestFrom(this.novaLocumsLeave1099GmcDialog$), takeUntilDestroyed(this.destroyRef))
            .subscribe(([_, isEnabled]) => {
                if (!isEnabled) {
                    return;
                }
                const initialLoadingData = this.marginCalculatorStore.initialLoadingData();
                if (!initialLoadingData) {
                    return;
                }
                this.eventBus.emit({
                    type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_LOAD_MARGIN_DETAILS,
                    payload: { marginDetails: initialLoadingData }
                });
            });
    }

    private translateSnapshotToMarginDetails(snapshot: LocumsMarginCalculatorSlice): MarginDetails {
        return {
            marginId: snapshot.marginSetup.marginId,
            jobDetails: snapshot.jobDetails,
            candidateDetails: snapshot.candidateDetails,
            marginSetUpDetails: (({ marginId, marginStatusId, ...rest }) => rest)(snapshot.marginSetup) as MarginSetup,
            marginCalculations: undefined,
            marginAdditionalRates: snapshot.additionalRates.additionalRates,
            marginExpenses: snapshot.expenses.marginExpenses,
            marginStatusId: snapshot.marginSetup.marginStatusId,
            marginContractDetails: snapshot.marginContract,
            candidateExpertiseId: snapshot.candidateDetails.candidateExpertiseId,
            expertiseId: snapshot.candidateDetails.expertiseId,
            expertiseYearsOfExperience: snapshot.candidateDetails.expertiseYearsOfExperience,
            notes: { marginNotes: snapshot.notes?.marginNotes ?? undefined },
            marginRtoDates: snapshot.marginRtoDates.rtoDates
        };
    }
}
